"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import {
  Compass,
  Home,
  Receipt,
  ShoppingBag,
  UserRound,
  type LucideIcon
} from "lucide-react";
import { createBrowserSupabase } from "@/lib/supabase/browser";
import { INVOICE_SUBTAB_STATUSES } from "@/lib/invoiceSubTabStatuses";
import { useCustomer } from "@/context/CustomerContext";

type NavItem = { label: string; href: string; icon: LucideIcon; badge: number };

/**
 * Halaman AKSI keranjang (Buat Nota Tagihan / Buat Nota + Kirim / Request
 * Kirim) — alur berlangkah dengan tombol utamanya di dasar layar.
 *
 * Bottom-nav disembunyikan di sana atas dua alasan yang keduanya praktis: ia
 * akan menumpuk dengan footer aksi yang lengket, dan satu ketukan salah di tab
 * "Pesanan" di tengah pengisian alamat membuang seluruh isian. Halamannya
 * punya tombol kembali sendiri, jadi tak ada jalan keluar yang hilang.
 */
function isCartActionPath(pathname: string): boolean {
  return (
    pathname === "/orders/tagihan-buat" ||
    pathname === "/orders/tagihan-kirim" ||
    pathname === "/orders/kirim"
  );
}

export function BottomNav() {
  const pathname = usePathname();
  const { customer, isLoading: customerLoading } = useCustomer();
  const [orderCount, setOrderCount] = useState(0);
  const [unpaidInvoices, setUnpaidInvoices] = useState(0);

  // Lencana "Pesanan" dari pesanan TAMU versi lama (localStorage) — hanya
  // untuk tamu, sama dengan /orders yang menampilkannya hanya tanpa sesi.
  // Pembeli yang masuk dulu melihat angka milik tamu lama di perangkat ini,
  // yang tak ada isinya di /orders (bughunt E9-11).
  const customerId = customer?.id ?? null;
  useEffect(() => {
    if (customerId || customerLoading) {
      setOrderCount(0);
      return;
    }
    try {
      const saved = window.localStorage.getItem("galea-preorder-orders");
      const orders = saved ? (JSON.parse(saved) as unknown[]) : [];
      setOrderCount(Array.isArray(orders) ? orders.length : 0);
    } catch {
      setOrderCount(0);
    }
  }, [customerId, customerLoading]);

  // Badge Tagihan = jumlah nota BELUM LUNAS milik pembeli — status yang SAMA
  // dengan badge & daftar sub-kolom Belum Lunas di /tagihan, termasuk nota
  // Menunggu ACC (bughunt E6-4: dulu open/partial saja, jadi nav "2" sementara
  // tab "3"). Head-count ringan, di-refresh tiap pindah halaman agar ikut
  // turun setelah bayar; dilewati di panel admin (scope sesi & RLS berbeda).
  useEffect(() => {
    if (
      !customer ||
      pathname.startsWith("/admin") ||
      pathname.startsWith("/toko") ||
      pathname.startsWith("/platform")
    ) {
      setUnpaidInvoices(0);
      return;
    }
    const db = createBrowserSupabase();
    if (!db) return;
    db.from("invoices")
      .select("id", { count: "exact", head: true })
      .eq("customer_id", customer.id)
      .in("status", INVOICE_SUBTAB_STATUSES.belum_lunas)
      .then(({ count }) => setUnpaidInvoices(count ?? 0));
  }, [customer?.id, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // /toko/* = Katalog Reseller white-label (mig 216) — navigasi toko utama
  // disembunyikan agar pembeli akhir tidak "nyasar" ke beranda/login toko.
  // /platform = halaman jualan platform di apex kelolify.com — bukan toko
  // sama sekali; nav toko di sana akan menautkan pengunjung ke domain yang salah.
  if (
    pathname.startsWith("/admin") ||
    pathname.startsWith("/toko") ||
    pathname.startsWith("/platform") ||
    isCartActionPath(pathname)
  ) {
    return null;
  }

  // Halaman Jelajah berlatar gelap layar penuh — nav ikut gelap supaya tak
  // tampak seperti pita putih yang memotong foto produk.
  const dark = pathname.startsWith("/jelajah");

  const renderItem = (item: NavItem) => {
    const Icon = item.icon;
    const isActive =
      item.href === "/"
        ? pathname === "/" || pathname.startsWith("/product")
        : pathname.startsWith(item.href);
    const idleClass = dark ? "text-white/60" : "text-muted";

    return (
      <Link
        key={item.href}
        href={item.href}
        // Nav ini ada di SEMUA halaman storefront; prefetch default = 4 request
        // RSC ke origin per halaman yang dibuka (target dinamis ber-middleware,
        // tak bisa di-cache). Matikan → fetch hanya saat tab benar-benar di-tap.
        prefetch={false}
        className={`flex min-h-12 flex-col items-center justify-center gap-1 rounded-md text-[11px] font-semibold transition ${
          isActive ? "text-shopee" : idleClass
        }`}
      >
        <div className="relative">
          <Icon size={21} strokeWidth={isActive ? 2.6 : 2} />
          {item.badge > 0 ? (
            <span className="absolute -right-2 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-shopee px-1 text-[9px] font-bold leading-none text-white">
              {item.badge > 99 ? "99+" : item.badge}
            </span>
          ) : null}
        </div>
        {/* Satu baris: label yang terbungkus menaikkan tinggi nav dan
            menumpuk konten di atasnya (lihat NAV_CLEARANCE Jelajah). */}
        <span className="whitespace-nowrap">{item.label}</span>
      </Link>
    );
  };

  // Urutan: Beranda · Jelajah · Pesanan · Tagihan · Akun Saya (Akun paling
  // kanan). Jelajah = feed produk vertikal ala media sosial (/jelajah).
  // Lonceng notifikasi TIDAK di sini sejak 2026-09-24 — pindah ke header, di
  // kiri ikon keranjang (`NotificationBell`, angkanya dari NotificationProvider).
  return (
    <nav
      // Penanda untuk aturan CSS yang menyembunyikan chrome toko di halaman
      // platform (globals.css). Dibutuhkan karena halaman itu disajikan lewat
      // REWRITE, sehingga penjaga berbasis pathname di atas tidak menyala.
      data-storefront-nav
      className={
        dark
          ? "fixed inset-x-0 bottom-0 z-40 mx-auto w-full max-w-md border-t border-white/10 bg-neutral-950/95 px-2 pb-3 pt-2 backdrop-blur safe-bottom"
          : "fixed inset-x-0 bottom-0 z-40 mx-auto w-full max-w-md border-t border-neutral-200 bg-white/95 px-2 pb-3 pt-2 shadow-[0_-10px_28px_rgba(34,33,38,0.08)] backdrop-blur safe-bottom"
      }
    >
      <div className="grid grid-cols-5">
        {renderItem({ label: "Beranda", href: "/", icon: Home, badge: 0 })}
        {renderItem({ label: "Jelajah", href: "/jelajah", icon: Compass, badge: 0 })}
        {renderItem({
          label: "Pesanan",
          href: "/orders",
          icon: ShoppingBag,
          badge: orderCount
        })}
        {renderItem({
          label: "Tagihan",
          href: "/tagihan",
          icon: Receipt,
          badge: unpaidInvoices
        })}
        {renderItem({
          label: "Akun Saya",
          href: "/account",
          icon: UserRound,
          badge: 0
        })}
      </div>
    </nav>
  );
}
